import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useStore } from 'react-redux';
import Button from '../../components/ui/Button';
import { Field, TextInput } from '../../components/ui/Field';
import { useOverlays } from '../../components/overlays/OverlayProvider';
import {
  adminInviteDetailsPost,
  forgotPasswordAdmin,
  resetPasswordAdmin,
  verifyUser,
} from '../../actions/authAction';
import StaarvaniLogo from '../../images/staarvani.png';
import './auth.css';

function Shell({ title, subtitle, error, notice, children }) {
  return (
    <div className="sv-auth">
      <aside className="sv-auth__panel">
        <img src={StaarvaniLogo} alt="Staarvani" className="sv-auth__logo" />
        <p className="sv-auth__copy">
          The operations desk behind every reading, report and partner on Staarvani.
        </p>
      </aside>
      <main className="sv-auth__main">
        <div className="sv-auth__form">
          <h1 className="sv-auth__title">{title}</h1>
          {subtitle && <p className="sv-auth__subtitle">{subtitle}</p>}
          {error && (
            <div className="sv-auth__banner" role="alert">
              <i className="bx bx-error-circle" aria-hidden="true" />
              {error}
            </div>
          )}
          {notice && (
            <div className="sv-auth__banner sv-auth__banner--ok" role="status">
              <i className="bx bx-check-circle" aria-hidden="true" />
              {notice}
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}

export function ForgotPassword() {
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await forgotPasswordAdmin(dispatch, { email });
      setSent(true);
    } catch {
      setError('Could not send the reset link. Check the address and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell
      title="Reset your password"
      subtitle="We will email you a link to set a new one."
      error={error}
      notice={sent ? `If ${email} has an account, a reset link is on its way.` : ''}
    >
      <form onSubmit={submit} style={{ display: 'contents' }}>
        <Field label="Email">
          <TextInput
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@staarvani.com"
            autoFocus
          />
        </Field>
        <Button variant="primary" type="submit" className="sv-auth__submit" loading={busy} disabled={!email}>
          Send reset link
        </Button>
        <Link className="sv-auth__link" to="/login">
          Back to sign in
        </Link>
      </form>
    </Shell>
  );
}

export function ResetPassword() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmValue, setConfirmValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setError('Use at least 8 characters.');
      return;
    }
    if (password !== confirmValue) {
      setError('Those passwords do not match.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await resetPasswordAdmin(dispatch, { token, password });
      navigate('/login');
    } catch {
      setError('That reset link is no longer valid. Request a new one.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell
      title="Choose a new password"
      subtitle="Pick something you have not used here before."
      error={error || (!token ? 'This link is missing its token. Request a new reset email.' : '')}
    >
      <form onSubmit={submit} style={{ display: 'contents' }}>
        <Field label="New password">
          <TextInput
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
          />
        </Field>
        <Field label="Confirm password">
          <TextInput
            type="password"
            autoComplete="new-password"
            value={confirmValue}
            onChange={(e) => setConfirmValue(e.target.value)}
          />
        </Field>
        <Button
          variant="primary"
          type="submit"
          className="sv-auth__submit"
          loading={busy}
          disabled={!token || !password || !confirmValue}
        >
          Set password
        </Button>
      </form>
    </Shell>
  );
}

/** Invite acceptance: verify the token, then set a password and name. */
export function Register() {
  const dispatch = useDispatch();
  const store = useStore();
  const navigate = useNavigate();
  const { toast } = useOverlays();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [invite, setInvite] = useState(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('This invite link is missing its token.');
      return;
    }
    adminInviteDetailsPost(dispatch, { token })
      .then((res) => {
        const data = res?.data || res;
        setInvite(data);
        setForm((f) => ({
          ...f,
          first_name: data?.first_name || '',
          last_name: data?.last_name || '',
        }));
      })
      .catch(() => setError('This invite is no longer valid. Ask an admin to send a new one.'));
  }, [token, dispatch]);

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      if (form.password.length < 8) {
        setError('Use at least 8 characters.');
        return;
      }
      setBusy(true);
      setError('');
      try {
        await verifyUser(dispatch, { token, ...form });

        // verifyUser reports failure through the store rather than throwing, so a
        // try/catch alone would show success for a rejected invite.
        const auth = store.getState()?.auth;
        if (auth?.error || !auth?.user_verified) {
          setError(
            auth?.errorMessage?.error ||
              'Could not complete the invite. Ask an admin to resend it.'
          );
          return;
        }

        toast('Account created — sign in to continue', { tone: 'success' });
        navigate('/login');
      } catch {
        setError('Could not complete the invite. Ask an admin to resend it.');
      } finally {
        setBusy(false);
      }
    },
    [dispatch, store, token, form, navigate, toast]
  );

  return (
    <Shell
      title="Accept your invite"
      subtitle={invite?.email ? `Setting up ${invite.email}` : 'Set your name and a password.'}
      error={error}
    >
      <form onSubmit={submit} style={{ display: 'contents' }}>
        <Field label="First name">
          <TextInput
            value={form.first_name}
            onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
          />
        </Field>
        <Field label="Last name">
          <TextInput
            value={form.last_name}
            onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
          />
        </Field>
        <Field label="Password">
          <TextInput
            type="password"
            autoComplete="new-password"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
          />
        </Field>
        <Button
          variant="primary"
          type="submit"
          className="sv-auth__submit"
          loading={busy}
          disabled={!token || !form.first_name || !form.password}
        >
          Create account
        </Button>
      </form>
    </Shell>
  );
}
